/**
 * Dynamic sign-up flow — frontend config reader.
 *
 * The backend delivers a `signup_flow` object on the initial-settings payload
 * (see CommonController@initialSettings). This module normalizes it and exposes
 * small helpers so the sign-up screen renders the right preset + attachments.
 *
 * Backward compatibility: when `signup_flow` is absent (older backend), every
 * helper falls back to the previous behaviour (passwordless preset, bonus
 * dropdown shown with all campaigns, social providers from the legacy
 * per-provider flags), so nothing regresses before the backend ships.
 */

export const SIGNUP_PRESETS = {
  PASSWORDLESS: "passwordless",
  FAST_EMAIL_PASSWORD: "fast_email_password",
  FAST_EMAIL_PASSWORD_DOB: "fast_email_password_dob",
  PROGRESSIVE_GEO: "progressive_email_password_dob_geo",
  FAST_EMAIL_PASSWORD_DOB_PHONE: "fast_email_password_dob_phone",
  FULL_SIGNUP: "full_signup",
};

const DEFAULT_FIELDS_BY_PRESET = {
  passwordless: ["email"],
  fast_email_password: ["email", "password"],
  fast_email_password_dob: ["email", "password", "date_of_birth"],
  progressive_email_password_dob_geo: ["email", "password", "date_of_birth", "country", "currency"],
  fast_email_password_dob_phone: ["email", "password", "date_of_birth", "phone"],
  full_signup: ["email", "nickname", "password", "date_of_birth", "phone", "address"],
};

/** The normalized signup_flow object, or null if not delivered. */
export function getSignupFlow(initialSettings) {
  const sf = initialSettings && initialSettings.signup_flow;
  if (!sf || typeof sf !== "object") return null;
  return sf;
}

/** Active preset key (defaults to passwordless = the live GCC flow). */
export function getActivePreset(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  return (sf && sf.preset) || SIGNUP_PRESETS.PASSWORDLESS;
}

/** Auth model for the active preset: 'otp' (passwordless) or 'password'. */
export function getAuthModel(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  if (sf && sf.auth) return sf.auth;
  return getActivePreset(initialSettings) === SIGNUP_PRESETS.PASSWORDLESS ? "otp" : "password";
}

/** Canonical field keys the active preset collects at sign-up time. */
export function getPresetFields(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  if (sf && Array.isArray(sf.fields) && sf.fields.length) return sf.fields;
  return DEFAULT_FIELDS_BY_PRESET[getActivePreset(initialSettings)] || ["email"];
}

export function presetHasField(initialSettings, field) {
  return getPresetFields(initialSettings).includes(field);
}

/**
 * IP-derived pre-fill hints for the sign-up form (progressive presets):
 * { country?: string, currency?: string }. Empty object when absent.
 */
export function getSignupPrefill(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  return sf && sf.prefill && typeof sf.prefill === "object" ? sf.prefill : {};
}

/** Allowed currency codes for the sign-up currency selector (progressive presets). */
export function getAllowedCurrencies(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  return sf && Array.isArray(sf.currencies) ? sf.currencies : [];
}

/**
 * Bonus dropdown config.
 * Legacy (no signup_flow): enabled with no curation (= show all), preserving
 * the current passwordless behaviour.
 */
export function getBonusDropdownConfig(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  if (!sf || !sf.bonus_dropdown) {
    return { enabled: true, campaignIds: [] };
  }
  const bd = sf.bonus_dropdown;
  return {
    enabled: !!bd.enabled,
    campaignIds: Array.isArray(bd.campaign_ids) ? bd.campaign_ids.map(Number) : [],
  };
}

export function isBonusDropdownEnabled(initialSettings) {
  return getBonusDropdownConfig(initialSettings).enabled;
}

/**
 * Curate the campaigns the back office selected. When the dropdown is disabled
 * → []. When enabled with an explicit curated list → only those ids. When
 * enabled with no curation → all active campaigns (legacy behaviour).
 */
export function filterCuratedCampaigns(campaigns, initialSettings) {
  const list = Array.isArray(campaigns) ? campaigns : [];
  const { enabled, campaignIds } = getBonusDropdownConfig(initialSettings);
  if (!enabled) return [];
  if (!campaignIds.length) return list;
  return list.filter((c) => campaignIds.includes(Number(c.id)));
}

/**
 * Enabled social providers as a lookup map { Google: true, Facebook: true, ... }.
 * Registry-driven: honours signup_flow.social_logins (master toggle + provider
 * list). Falls back to the legacy per-provider flags when signup_flow is absent.
 */
export function getEnabledSocialProviders(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  if (sf && sf.social_logins) {
    if (!sf.social_logins.enabled) return {};
    const out = {};
    (Array.isArray(sf.social_logins.providers) ? sf.social_logins.providers : []).forEach(
      (key) => {
        out[key] = true;
      }
    );
    return out;
  }
  // Legacy fallback: derive from the old boolean flags.
  const legacy = {};
  if (initialSettings && initialSettings.google_login_enabled) legacy.Google = true;
  if (initialSettings && initialSettings.facebook_login_enabled) legacy.Facebook = true;
  if (initialSettings && initialSettings.apple_login_enabled) legacy.Apple = true;
  return legacy;
}

export function isSocialProviderEnabled(initialSettings, providerKey) {
  return !!getEnabledSocialProviders(initialSettings)[providerKey];
}

/** Explainer image URL for the active sign-up type, or null. */
export function getExplainerImageUrl(initialSettings) {
  const sf = getSignupFlow(initialSettings);
  return (sf && sf.explainer_image_url) || null;
}
